<template>
  <div class="ai-dialog-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>AI口语教师</h2>
      <p>与AI进行英语口语练习，提升您的英语表达能力</p>
    </div>

    <!-- 用户等级选择 -->
    <div class="user-level-section">
      <el-card class="level-card">
        <div slot="header">
          <span>选择您的学习等级</span>
        </div>
        <el-radio-group v-model="userLevel" @change="onUserLevelChange">
          <el-radio label="0">1-3年级小学生</el-radio>
          <el-radio label="1">4-6年级小学生</el-radio>
          <el-radio label="2">初中学生</el-radio>
          <el-radio label="3">高中学生</el-radio>
        </el-radio-group>
      </el-card>
    </div>

    <!-- 场景选择区域 -->
    <div v-if="!currentScene" class="scene-selection">
      <el-card class="scene-card">
        <div slot="header">
          <span>选择练习场景</span>
          <el-button style="float: right; padding: 3px 0" type="text" @click="refreshTopics">
            刷新场景
          </el-button>
        </div>

        <!-- 默认场景列表 -->
        <div v-if="topicList.length > 0" class="topic-list">
          <div v-for="group in topicList" :key="group.tag" class="topic-group">
            <h3>{{ group.tag }}</h3>
            <div class="topic-items">
              <el-button
                v-for="topic in group.topics"
                :key="topic.enName"
                type="primary"
                plain
                @click="selectTopic(topic)"
                class="topic-item"
              >
                <span class="topic-emoji">{{ topic.emoji }}</span>
                <span class="topic-name">{{ topic.name }}</span>
                <span class="topic-en-name">{{ topic.enName }}</span>
              </el-button>
            </div>
          </div>
        </div>

        <!-- 自定义场景 -->
        <div class="custom-topic">
          <h3>自定义场景</h3>
          <el-input
            v-model="customTopic"
            placeholder="请输入您想要练习的话题（英文）"
            @keyup.enter.native="generateCustomTopic"
          >
            <el-button slot="append" @click="generateCustomTopic">生成场景</el-button>
          </el-input>
        </div>
      </el-card>
    </div>

    <!-- 对话练习区域 -->
    <div v-if="currentScene" class="dialog-practice">
      <el-card class="dialog-card">
        <div slot="header">
          <span>对话练习 - {{ currentScene.content }}</span>
          <el-button style="float: right; padding: 3px 0" type="text" @click="resetDialog">
            重新开始
          </el-button>
        </div>

        <!-- 场景信息 -->
        <div class="scene-info">
          <div class="scene-main">
            <span class="scene-emoji">{{ currentScene.emoji }}</span>
            <div class="scene-details">
              <h4>{{ currentScene.mainTask }}</h4>
              <p><strong>您的角色：</strong>{{ currentScene.userRole }}</p>
              <p><strong>AI角色：</strong>{{ currentScene.botRole }} ({{ currentScene.botGender }})</p>
            </div>
          </div>

          <div class="scene-tasks">
            <h5>练习任务：</h5>
            <ul>
              <li v-for="(task, index) in currentScene.subTasks" :key="index">
                {{ task }}
              </li>
            </ul>
          </div>
        </div>

        <!-- 对话历史 -->
        <div class="dialog-history">
          <div
            v-for="(message, index) in dialogHistory"
            :key="index"
            :class="['message', message.speaker === 'System' ? 'ai-message' : 'user-message']"
          >
            <div class="message-avatar">
              <i v-if="message.speaker === 'System'" class="el-icon-user-solid"></i>
              <i v-else class="el-icon-user"></i>
            </div>
            <div class="message-content">
              <div class="message-text">{{ message.content }}</div>
              <div class="message-time">{{ formatTime(message.timestamp) }}</div>
            </div>
          </div>
        </div>

        <!-- 用户输入区域 -->
        <div class="user-input">
          <el-input
            v-model="userInput"
            type="textarea"
            :rows="3"
            placeholder="请输入您的回复（英文）"
            @keyup.ctrl.enter.native="sendMessage"
          />
          <div class="input-actions">
            <el-button type="primary" @click="sendMessage" :loading="loading">
              发送回复
            </el-button>
            <el-button @click="getRecommendation" :disabled="!canGetRecommendation">
              获取建议
            </el-button>
            <el-button type="success" @click="generateFinalReport" :disabled="dialogHistory.length < 2">
              生成报告
            </el-button>
          </div>
        </div>

        <!-- 推荐表达 -->
        <div v-if="recommendations.length > 0" class="recommendations">
          <h4>推荐表达：</h4>
          <div class="recommendation-list">
            <el-tag
              v-for="(rec, index) in recommendations"
              :key="index"
              type="success"
              @click="useRecommendation(rec)"
              class="recommendation-item"
            >
              {{ rec }}
            </el-tag>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 报告展示区域 -->
    <div v-if="report" class="report-section">
      <el-card class="report-card">
        <div slot="header">
          <span>学习报告</span>
        </div>

        <div class="report-summary">
          <el-row :gutter="20">
            <el-col :span="6">
              <div class="score-item">
                <div class="score-value">{{ report.summary.grammarScore }}</div>
                <div class="score-label">语法得分</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="score-item">
                <div class="score-value">{{ report.summary.voiceScore }}</div>
                <div class="score-label">语音得分</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="score-item">
                <div class="score-value">{{ report.summary.wordNum }}</div>
                <div class="score-label">单词数</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="score-item">
                <div class="score-value">{{ report.summary.star }}/3</div>
                <div class="score-label">任务完成度</div>
              </div>
            </el-col>
          </el-row>
        </div>

        <div class="report-details">
          <div v-for="item in report.history" :key="item.index" class="history-item">
            <div class="history-header">
              <span class="history-speaker">{{ item.speaker }}</span>
              <span class="history-content">{{ item.content }}</span>
            </div>

            <div v-if="item.suggest" class="history-suggestions">
              <div v-if="item.suggest.grammar" class="grammar-suggestion">
                <h5>语法建议：</h5>
                <p>{{ item.suggest.grammar.advice }}</p>
                <p>得分：{{ item.suggest.grammar.score }}</p>
              </div>

              <div v-if="item.suggest.voice" class="voice-suggestion">
                <h5>语音建议：</h5>
                <p>总体得分：{{ item.suggest.voice.overall }}</p>
                <div v-if="item.suggest.voice.words" class="word-suggestions">
                  <div v-for="word in item.suggest.voice.words" :key="word.word" class="word-item">
                    <strong>{{ word.word }}：</strong>{{ word.text }}
                  </div>
                </div>
              </div>
            </div>

            <div v-if="item.paraphrase && item.paraphrase.length > 0" class="paraphrase-suggestions">
              <h5>润色建议：</h5>
              <div v-for="(para, index) in item.paraphrase" :key="index" class="paraphrase-item">
                <p><strong>原句：</strong>{{ para.rawSent }}</p>
                <div v-if="para.diffs" class="diff-list">
                  <span
                    v-for="(diff, diffIndex) in para.diffs"
                    :key="diffIndex"
                    :class="['diff-item', `diff-${diff.operation.toLowerCase()}`]"
                  >
                    {{ diff.text }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { getDefaultTopics, generateTopic, generateDialog, generateRecommendation, generateReport } from '@/api/aiDialog'

export default {
  name: 'AIDialog',
  data() {
    return {
      userLevel: '2', // 默认初中学生
      topicList: [],
      customTopic: '',
      currentScene: null,
      taskId: '',
      dialogHistory: [],
      userInput: '',
      loading: false,
      recommendations: [],
      report: null
    }
  },
  computed: {
    canGetRecommendation() {
      return this.dialogHistory.length > 0 && this.dialogHistory.length % 2 === 0
    }
  },
  mounted() {
    this.loadDefaultTopics()
  },
  methods: {
    // 加载默认场景列表
    async loadDefaultTopics() {
      try {
        this.loading = true
        const response = await getDefaultTopics()
        if (response.code === 1 && response.data && response.data.data) {
          this.topicList = response.data.data.topicList || []
        }
      } catch (error) {
        this.$message.error('加载场景列表失败：' + error.message)
      } finally {
        this.loading = false
      }
    },

    // 刷新场景列表
    refreshTopics() {
      this.loadDefaultTopics()
    },

    // 选择预设场景
    async selectTopic(topic) {
      try {
        this.loading = true
        const response = await generateTopic({ topic: topic.enName })
        if (response.code === 1 && response.data && response.data.data) {
          this.currentScene = response.data.data.scene
          this.taskId = response.data.data.taskId
          this.dialogHistory = []
          this.report = null
          this.recommendations = []

          // 生成第一句话
          await this.generateFirstMessage()
        }
      } catch (error) {
        this.$message.error('生成场景失败：' + error.message)
      } finally {
        this.loading = false
      }
    },

    // 生成自定义场景
    async generateCustomTopic() {
      if (!this.customTopic.trim()) {
        this.$message.warning('请输入话题内容')
        return
      }

      try {
        this.loading = true
        const response = await generateTopic({ topic: this.customTopic.trim() })
        if (response.code === 1 && response.data && response.data.data) {
          this.currentScene = response.data.data.scene
          this.taskId = response.data.data.taskId
          this.dialogHistory = []
          this.report = null
          this.recommendations = []

          // 生成第一句话
          await this.generateFirstMessage()
        }
      } catch (error) {
        this.$message.error('生成自定义场景失败：' + error.message)
      } finally {
        this.loading = false
      }
    },

    // 生成第一句话
    async generateFirstMessage() {
      try {
        const response = await generateDialog({
          taskId: this.taskId,
          userLevel: this.userLevel,
          scene: this.currentScene,
          history: []
        })

        if (response.code === 1 && response.data && response.data.data && response.data.data.resultArr) {
          const firstMessage = response.data.data.resultArr[0]
          if (firstMessage && firstMessage.result && firstMessage.result.length > 0) {
            this.dialogHistory.push({
              speaker: 'System',
              content: firstMessage.result[0],
              timestamp: new Date()
            })
          }
        }
      } catch (error) {
        this.$message.error('生成第一句话失败：' + error.message)
      }
    },

    // 发送用户消息
    async sendMessage() {
      if (!this.userInput.trim()) {
        this.$message.warning('请输入回复内容')
        return
      }

      // 添加用户消息到历史
      this.dialogHistory.push({
        speaker: 'User',
        content: this.userInput.trim(),
        timestamp: new Date()
      })

      const userMessage = this.userInput.trim()
      this.userInput = ''

      try {
        this.loading = true

        // 构建对话历史
        const history = this.dialogHistory.map(msg => ({
          speaker: msg.speaker,
          content: msg.content
        }))

        const response = await generateDialog({
          taskId: this.taskId,
          userLevel: this.userLevel,
          scene: this.currentScene,
          history: history
        })

        if (response.code === 1 && response.data && response.data.data && response.data.data.resultArr) {
          const aiMessage = response.data.data.resultArr[0]
          if (aiMessage && aiMessage.result && aiMessage.result.length > 0) {
            this.dialogHistory.push({
              speaker: 'System',
              content: aiMessage.result[0],
              timestamp: new Date()
            })
          }

          // 检查是否结束
          if (response.data.data.endCode === 1) {
            this.$message.success('对话已结束，可以生成报告了')
          }
        }
      } catch (error) {
        this.$message.error('发送消息失败：' + error.message)
      } finally {
        this.loading = false
      }
    },

    // 获取推荐表达
    async getRecommendation() {
      if (!this.canGetRecommendation) {
        this.$message.warning('无法获取推荐表达')
        return
      }

      try {
        this.loading = true

        const history = this.dialogHistory.map(msg => ({
          speaker: msg.speaker,
          content: msg.content
        }))

        const response = await generateRecommendation({
          taskId: this.taskId,
          userLevel: this.userLevel,
          scene: this.currentScene,
          history: history,
          indexArr: [String(this.dialogHistory.length - 1)],
          count: '3'
        })

        if (response.code === 1 && response.data && response.data.data && response.data.data.resultArr) {
          const recommendation = response.data.data.resultArr[0]
          if (recommendation && recommendation.result) {
            this.recommendations = recommendation.result
          }
        }
      } catch (error) {
        this.$message.error('获取推荐表达失败：' + error.message)
      } finally {
        this.loading = false
      }
    },

    // 使用推荐表达
    useRecommendation(recommendation) {
      this.userInput = recommendation
      this.recommendations = []
    },

    // 生成最终报告
    async generateFinalReport() {
      try {
        this.loading = true

        const history = this.dialogHistory.map(msg => ({
          speaker: msg.speaker,
          content: msg.content
        }))

        const response = await generateReport({
          taskId: this.taskId,
          userLevel: this.userLevel,
          scene: this.currentScene,
          history: history
        })

        if (response.code === 1 && response.data && response.data.data) {
          this.report = response.data.data
          this.$message.success('报告生成成功')
        }
      } catch (error) {
        this.$message.error('生成报告失败：' + error.message)
      } finally {
        this.loading = false
      }
    },

    // 重置对话
    resetDialog() {
      this.currentScene = null
      this.taskId = ''
      this.dialogHistory = []
      this.userInput = ''
      this.recommendations = []
      this.report = null
    },

    // 用户等级变化
    onUserLevelChange() {
      if (this.currentScene) {
        this.$message.info('用户等级已更改，建议重新开始对话')
      }
    },

    // 格式化时间
    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString()
    }
  }
}
</script>

<style scoped>
.ai-dialog-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
}

.page-header h2 {
  color: #303133;
  margin-bottom: 10px;
}

.page-header p {
  color: #606266;
  font-size: 14px;
}

.user-level-section {
  margin-bottom: 30px;
}

.level-card {
  margin-bottom: 20px;
}

.scene-selection {
  margin-bottom: 30px;
}

.scene-card {
  margin-bottom: 20px;
}

.topic-list {
  margin-bottom: 30px;
}

.topic-group {
  margin-bottom: 20px;
}

.topic-group h3 {
  color: #303133;
  margin-bottom: 15px;
  border-bottom: 2px solid #409EFF;
  padding-bottom: 5px;
}

.topic-items {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.topic-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 150px;
  height: 80px;
  justify-content: center;
}

.topic-emoji {
  font-size: 20px;
  margin-bottom: 5px;
}

.topic-name {
  font-size: 12px;
  color: #303133;
}

.topic-en-name {
  font-size: 10px;
  color: #909399;
  margin-top: 2px;
}

.custom-topic {
  border-top: 1px solid #EBEEF5;
  padding-top: 20px;
}

.custom-topic h3 {
  color: #303133;
  margin-bottom: 15px;
}

.dialog-practice {
  margin-bottom: 30px;
}

.dialog-card {
  margin-bottom: 20px;
}

.scene-info {
  background: #f5f7fa;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.scene-main {
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
}

.scene-emoji {
  font-size: 30px;
  margin-right: 15px;
}

.scene-details h4 {
  color: #303133;
  margin-bottom: 10px;
}

.scene-details p {
  margin: 5px 0;
  color: #606266;
}

.scene-tasks h5 {
  color: #303133;
  margin-bottom: 10px;
}

.scene-tasks ul {
  margin: 0;
  padding-left: 20px;
}

.scene-tasks li {
  color: #606266;
  margin-bottom: 5px;
}

.dialog-history {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 20px;
  background: #fafafa;
}

.message {
  display: flex;
  margin-bottom: 15px;
  align-items: flex-start;
}

.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
}

.ai-message .message-avatar {
  background: #409EFF;
  color: white;
}

.user-message .message-avatar {
  background: #67C23A;
  color: white;
}

.message-content {
  flex: 1;
  background: white;
  padding: 10px 15px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.ai-message .message-content {
  border-left: 3px solid #409EFF;
}

.user-message .message-content {
  border-left: 3px solid #67C23A;
}

.message-text {
  color: #303133;
  line-height: 1.5;
  margin-bottom: 5px;
}

.message-time {
  font-size: 12px;
  color: #909399;
}

.user-input {
  margin-bottom: 20px;
}

.input-actions {
  margin-top: 10px;
  display: flex;
  gap: 10px;
}

.recommendations {
  background: #f0f9ff;
  padding: 15px;
  border-radius: 4px;
  border: 1px solid #b3d8ff;
}

.recommendations h4 {
  color: #303133;
  margin-bottom: 10px;
}

.recommendation-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.recommendation-item {
  cursor: pointer;
  transition: all 0.3s;
}

.recommendation-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.report-section {
  margin-bottom: 30px;
}

.report-card {
  margin-bottom: 20px;
}

.report-summary {
  margin-bottom: 30px;
}

.score-item {
  text-align: center;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 8px;
}

.score-value {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 5px;
}

.score-label {
  color: #606266;
  font-size: 14px;
}

.report-details {
  border-top: 1px solid #EBEEF5;
  padding-top: 20px;
}

.history-item {
  margin-bottom: 20px;
  padding: 15px;
  background: #fafafa;
  border-radius: 4px;
}

.history-header {
  margin-bottom: 10px;
}

.history-speaker {
  font-weight: bold;
  color: #409EFF;
  margin-right: 10px;
}

.history-content {
  color: #303133;
}

.history-suggestions {
  margin-top: 10px;
}

.grammar-suggestion,
.voice-suggestion {
  margin-bottom: 15px;
}

.grammar-suggestion h5,
.voice-suggestion h5 {
  color: #303133;
  margin-bottom: 5px;
}

.word-suggestions {
  margin-top: 10px;
}

.word-item {
  margin-bottom: 5px;
  color: #606266;
}

.paraphrase-suggestions {
  margin-top: 15px;
  padding: 10px;
  background: #fff3cd;
  border-radius: 4px;
  border: 1px solid #ffeaa7;
}

.paraphrase-suggestions h5 {
  color: #856404;
  margin-bottom: 10px;
}

.paraphrase-item {
  margin-bottom: 10px;
}

.diff-list {
  margin-top: 5px;
}

.diff-item {
  margin-right: 5px;
  padding: 2px 4px;
  border-radius: 2px;
}

.diff-equal {
  background: #d4edda;
  color: #155724;
}

.diff-delete {
  background: #f8d7da;
  color: #721c24;
  text-decoration: line-through;
}

.diff-insert {
  background: #d1ecf1;
  color: #0c5460;
  font-weight: bold;
}
</style>
